import {Autocomplete, FormControl, TextField,} from '@mui/material';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {DataSource} from '../../models/models';

interface DataSourceMultiSelectProps {
    dataSources: DataSource[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
}

const DataSourceMultiSelect = ({
    dataSources,
    selectedIds,
    onChange,
}: DataSourceMultiSelectProps) => {
    const {t} = useTranslation();

    const uniqueDataSources = useMemo(() => {
        const seen = new Set();
        return dataSources.filter(ds => {
            if (seen.has(ds.id)) return false;
            seen.add(ds.id);
            return true;
        });
    }, [dataSources]);

    const handleSingleChange = useCallback(
        (
            _: React.SyntheticEvent,
            value: DataSource | null
        ) => {
            onChange(value ? [value.id] : []);
        },
        [onChange]
    );

    const singleValue = useMemo(
        () => uniqueDataSources.find(source => selectedIds.includes(source.id)) ?? null,
        [uniqueDataSources, selectedIds]
    );

    return (
        <FormControl
            sx={{
                m: 1,
                width: '96%',
                margin: '8px 0',
                '&:not(:focus-within) .MuiInputLabel-root': {
                    top: '-3px'
                }
            }}
        >
            <Autocomplete
                id="dataSources"
                options={uniqueDataSources}
                value={singleValue}
                onChange={handleSingleChange}
                getOptionLabel={(source) => source.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                    <li {...props} key={`${option.id}-${option.name}`}>
                        {option.name}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        label={t('multiSelect.dataSources')}
                    />
                )}
            />
        </FormControl>
    );
};

export default DataSourceMultiSelect;
